// ADR-0014 §3 / §12 B-2 — the three systems that can master a tenant's class-III fields (a person's
// org-unit placement, a unit's manager, and — when they get columns — job title and employment dates),
// as the UI names them.
//
// The source of truth for WHICH codes exist is the backend's `ClassIiiMasters`
// (backend/src/Clms.Domain/Entities/ClassIiiMastership.cs), where the vocabulary is closed by
// `ck_tenants_class_iii_master`. This list must cover that set exactly, and
// `src/i18n/class-iii-master-i18n.test.mjs` reads the C# file and both locale files and fails the build
// when it does not — because the failure mode is a page that says a field is read-only without being able
// to say who owns it, which is precisely the generic save error §3 asks us not to show.

/** Every master the backend can report, in the order ADR-0014 §3's table lists them. */
export const CLASS_III_MASTERS = ["tenant_admin", "customer_hr_system", "verso_hrm"] as const;

export type ClassIiiMasterCode = (typeof CLASS_III_MASTERS)[number];

/** The one master that means "writable here, by hand" — the default, and the pre-B-2 behaviour. */
export const CLASS_III_MASTER_TENANT_ADMIN: ClassIiiMasterCode = "tenant_admin";

/** Is this code one the vocabulary names (and can therefore be labelled)? */
export function hasClassIiiMasterLabel(code: string): code is ClassIiiMasterCode {
  return (CLASS_III_MASTERS as readonly string[]).includes(code);
}

/**
 * The i18n key naming the system that owns the class-III fields.
 *
 * A code the vocabulary does not know falls back to `org.classIii.master.unknown` rather than rendering
 * the raw code: the sentence this label lands in is "these fields are maintained in X", and an untranslated
 * database value there reads as a bug to the administrator — while "another system" is at least true. The
 * i18n guard is what keeps that fallback unreachable.
 */
export function classIiiMasterLabelKey(code: string): string {
  return hasClassIiiMasterLabel(code)
    ? `org.classIii.master.${code}`
    : "org.classIii.master.unknown";
}

/**
 * The owning system carried by a refused class-III write: the `classIiiMaster` problem extension on the
 * 409 from `AdminOrgEndpoints.MasteredElsewhere`. Null when the response is not that refusal, which is how
 * a caller tells "owned elsewhere" from an ordinary failure without matching on a message.
 */
export function classIiiMasterOfRefusal(problem: Record<string, unknown> | undefined): string | null {
  const value = problem?.["classIiiMaster"];
  return typeof value === "string" && value.length > 0 ? value : null;
}
